import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Pre-fetches a bare Stripe PaymentIntent for the booking deposit so the
 * Payment Element is ready the instant the patient taps "Book Now".
 *
 * POST /api/prepare-booking-payment is called on mount (once quoteId is
 * truthy) and again whenever promoCode, giftPassCode, or bookingPriceCents
 * changes — the server updates the SAME PaymentIntent, so re-prepares are cheap.
 *
 * Status machine:
 *   "idle"        — quoteId not yet available; nothing fired
 *   "preparing"   — fetch in flight
 *   "ready"       — payload (clientSecret + publishableKey + amountCents) available
 *   "unavailable" — server returned 404 (kill switch off); permanent for session
 *   "error"       — network/5xx; caller falls back to the legacy click path
 *
 * Stale-promo race: every fetch captures an inputsKey at fire time and
 * compares it with the key when the response arrives. Responses for superseded
 * inputs are silently discarded.
 *
 * Never throws. Always falls back to the legacy /api/create-booking-invoice
 * click path on "unavailable" or "error".
 */

export interface PreparedPaymentPayload {
  clientSecret: string;
  publishableKey: string;
  amountCents: number;
}

export type PreparedPaymentStatus =
  | "idle"
  | "preparing"
  | "ready"
  | "unavailable"
  | "error";

interface UsePreparedBookingPaymentArgs {
  quoteId: string | null | undefined;
  promoCode?: string | null;
  giftPassCode?: string | null;
  // The booking tier selector in cents ($1 admin / $99 held F&F pass / $500
  // standard). MUST be threaded to the prepare endpoint: without it the server
  // defaults requestedCents to $500 and the prepared PI silently charges $500
  // for a gift/admin booking (the 2026-06 gift-$99 bug). The server validates
  // it against an allow-list, so it is a hint, never a trusted price.
  bookingPriceCents?: number | null;
}

interface UsePreparedBookingPaymentResult {
  payload: PreparedPaymentPayload | null;
  status: PreparedPaymentStatus;
  /** The inputs key that was used to build the current payload — compare
   *  against the CURRENT promo/gift-pass before trusting the payload in
   *  handleClick to detect a change that hasn't re-prepared yet. */
  inputsKey: string;
  /** Discard the prepared payload — called after a chunk-load failure so the
   *  next click takes the legacy fetch path instead of replaying the stale
   *  prepared secret. */
  discard: () => void;
}

// Exported so BookNowButton's click-time fast-path guard builds the SAME key
// from the SAME inputs. Two independently hand-written key strings is exactly
// how the prepared PI and the click path drifted apart in the 2026-06 gift-$99
// bug — sharing one builder makes that drift impossible.
export function buildBookingInputsKey(
  quoteId: string | null | undefined,
  promoCode?: string | null,
  giftPassCode?: string | null,
  bookingPriceCents?: number | null,
): string {
  return `${quoteId ?? ""}|${promoCode ?? ""}|${giftPassCode ?? ""}|${
    bookingPriceCents ?? ""
  }`;
}

export function usePreparedBookingPayment({
  quoteId,
  promoCode,
  giftPassCode,
  bookingPriceCents,
}: UsePreparedBookingPaymentArgs): UsePreparedBookingPaymentResult {
  const [status, setStatus] = useState<PreparedPaymentStatus>("idle");
  const [payload, setPayload] = useState<PreparedPaymentPayload | null>(null);
  const [inputsKey, setInputsKey] = useState(() =>
    buildBookingInputsKey(quoteId, promoCode, giftPassCode, bookingPriceCents),
  );

  // "unavailable" is permanent for the session — the kill switch is off.
  const unavailableRef = useRef(false);

  // Prevent concurrent in-flight fetches for the same inputs.
  const inFlightKeyRef = useRef<string | null>(null);

  const discard = useCallback(() => {
    setPayload(null);
    setStatus("error");
  }, []);

  useEffect(() => {
    if (!quoteId) return;
    // If the kill switch is known to be off, don't retry.
    if (unavailableRef.current) return;

    const key = buildBookingInputsKey(
      quoteId,
      promoCode,
      giftPassCode,
      bookingPriceCents,
    );

    // Single-flight: if there's already an in-flight request for these exact
    // inputs, don't fire a duplicate.
    if (inFlightKeyRef.current === key) return;

    let cancelled = false;
    inFlightKeyRef.current = key;
    setStatus("preparing");
    setInputsKey(key);

    (async () => {
      try {
        const body: Record<string, string | number> = { quoteId };
        if (promoCode) body.promoCode = promoCode;
        if (giftPassCode) body.giftPassCode = giftPassCode;
        // Send as a JSON NUMBER, not a string: the server validates the tier
        // with allowedAmounts.includes(bookingPriceCents) (strict ===), so a
        // string "9900" would NOT match and would silently fall back to $500.
        if (typeof bookingPriceCents === "number") {
          body.bookingPriceCents = bookingPriceCents;
        }

        const res = await fetch(
          `${process.env.REACT_APP_API_URL || ""}/api/prepare-booking-payment`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include", // carries admin cookie for $1 test mode
            body: JSON.stringify(body),
          },
        );

        // Stale-response guard: if a newer key is already in flight (or was
        // resolved), discard this response.
        if (cancelled) return;

        if (res.status === 404) {
          // Kill switch off — permanent for this session; don't retry.
          unavailableRef.current = true;
          inFlightKeyRef.current = null;
          setStatus("unavailable");
          setPayload(null);
          return;
        }

        if (!res.ok) {
          inFlightKeyRef.current = null;
          setStatus("error");
          setPayload(null);
          return;
        }

        const data = await res.json();
        if (cancelled) return;

        // Validate the response has everything the panel needs.
        if (
          !data?.ok ||
          !data?.clientSecret ||
          !data?.publishableKey ||
          typeof data?.amountCents !== "number"
        ) {
          inFlightKeyRef.current = null;
          setStatus("error");
          setPayload(null);
          return;
        }

        // Staleness is handled by `cancelled` alone: when the inputs change,
        // this effect's cleanup runs (setting cancelled=true) BEFORE the new
        // effect fires, so a late response from the old inputs can never get
        // past the guards above. (Re-deriving the key here from closure
        // variables would always equal `key` — the closure is from the same
        // render — so a key comparison adds nothing.)
        inFlightKeyRef.current = null;
        setPayload({
          clientSecret: data.clientSecret,
          publishableKey: data.publishableKey,
          amountCents: data.amountCents,
        });
        setInputsKey(key);
        setStatus("ready");
      } catch {
        // Network error / JSON parse error — not a kill-switch signal.
        if (cancelled) return;
        inFlightKeyRef.current = null;
        setStatus("error");
        setPayload(null);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Deps intentionally limited to the prepare INPUTS. No eslint-disable
    // directive here: the react-hooks plugin is not installed and the prod
    // webpack ESLint pass hard-fails on directives naming unknown rules
    // (see the #395/#399 deploy incident + ReconcileDialog convention).
  }, [quoteId, promoCode, giftPassCode, bookingPriceCents]);

  return { payload, status, inputsKey, discard };
}
